import React, { useState, useEffect, useCallback } from 'react';
import { Card, Box, Button, InlineStack, Badge, Link, ButtonGroup, Modal, TextField, Select, FormLayout, Text, DataTable, Tabs, Banner } from '@shopify/polaris';
import { getRequest, postRequest, putRequest, getCurrentDateTimeLocal } from '../../components/functions';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { employeeInteractionTypes, employeeInteractionTypeGroups } from '../../../pages/interactions/components/InteractionForm';
import InteractionDocuments from '../../../pages/interaction/components/InteractionDocuments';
import { useLookupStore } from '../../../context/useStore.js';
import { CardHeading } from '../cards/CardHeading.jsx';

export default function EmployeeTimeline({ employeeId }) {
  const [timelineItems, setTimelineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [modalTab, setModalTab] = useState(0);
  const [error, setError] = useState(null);
  const [savingResult, setSavingResult] = useState(null);
  const { users } = useLookupStore();

  const handleModalTabChange = useCallback((selectedTabIndex) => {
    setModalTab(selectedTabIndex);
  }, []);

  useEffect(() => {
    if (employeeId && employeeId !== 'new') {
      fetchTimeline();
    }
  }, [employeeId]);

  const fetchTimeline = async () => {
    if (employeeId === 'new' || !employeeId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await getRequest(`/api/users/${employeeId}/timeline?limit=50`);
      setTimelineItems(Array.isArray(data.timeline) ? data.timeline : []);
    } catch (error) {
      console.error('Error fetching employee timeline:', error);
      setTimelineItems([]);
      setError('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  const getInteractionBadgeColor = (type) => {
    if (!type) return 'default';

    for (const group of Object.values(employeeInteractionTypeGroups)) {
      if (group.types.includes(type)) {
        return group.color;
      }
    }
    return 'default';
  };

  const getInitialInteraction = () => ({
    type: '',
    description: '',
    event_date: getCurrentDateTimeLocal(),
    employee_id: employeeId,
    title: ''
  });

  const handleSaveInteraction = async (interaction) => {
    setSavingResult(null);
    try {
      if (interaction.id) {
        // Update existing interaction
        await putRequest(`/api/interactions/${interaction.id}`, interaction);
      } else {
        // Create new interaction
        await postRequest('/api/interactions', interaction);
      }
      await fetchTimeline();
      setIsViewModalOpen(false);
      setIsNewModalOpen(false);
      setEditingInteraction(null);
      setSavingResult('success');
    } catch (error) {
      console.error('Error saving interaction:', error);
      setSavingResult('error');
      throw error;
    }
  };

  if (employeeId === 'new' || !employeeId) {
    return null;
  }

  if (loading && timelineItems.length === 0) {
    return (
      <Card>
        <Box>
          <Box paddingBlockEnd="200">
            <InlineStack align="space-between">
              <CardHeading title="Employee Timeline" />
              <Button primary onClick={() => setIsNewModalOpen(true)} loading={loading}>
                New Event
              </Button>
            </InlineStack>
          </Box>
          <div style={{ padding: '16px' }}>Loading timeline...</div>
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Box>
          <Box paddingBlockEnd="200">
            <InlineStack align="space-between">
              <CardHeading title="Employee Timeline" />
              <Button primary onClick={() => setIsNewModalOpen(true)}>
                New Event
              </Button>
            </InlineStack>
          </Box>
          <div style={{ padding: '16px', color: 'rgb(220, 53, 69)' }}>
            {error}. <Link onClick={fetchTimeline}>Try again</Link>
          </div>
        </Box>
      </Card>
    );
  }

  const rows = timelineItems.length
    ? timelineItems.map((item) => {
        const isSystemEvent = item.source_type === 'event';

        if (isSystemEvent) {
          // Render system event row
          return [
            String(item.timestamp || '')
              .replace('T', ' ')
              .slice(0, 16),
            <Badge tone="info">System Event</Badge>,
            <Text as="span" variant="bodyMd">
              {item.type || 'Unknown'}
            </Text>,
            item.user_abbreviation || 'System',
            <Text as="span" tone="subdued">
              Auto-generated
            </Text>
          ];
        } else {
          // Count attached documents
          let docCount = 0;
          if (item.S3ObjectKeys) {
            try {
              const keys = JSON.parse(item.S3ObjectKeys);
              docCount = Array.isArray(keys) ? keys.length : 0;
            } catch (e) {
              // Invalid JSON, ignore
            }
          }

          // Render interaction row
          return [
            String(item.event_date || '')
              .replace('T', ' ')
              .slice(0, 16),
            <Badge tone={getInteractionBadgeColor(item.type)}>{item.type || 'Unknown'}</Badge>,
            <InlineStack gap="200" blockAlign="center">
              <Text as="span" variant="bodyMd">
                {item.title?.length > 60 ? `${item.title.slice(0, 60)}...` : item.title || 'No title'}
              </Text>
              {docCount > 0 && <Badge tone="success">📎 {docCount}</Badge>}
            </InlineStack>,
            item.user_abbreviation || 'N/A',
            <ButtonGroup>
              <Button
                size="slim"
                onClick={() => {
                  setEditingInteraction(item);
                  setModalTab(0);
                  setIsViewModalOpen(true);
                }}
              >
                Edit
              </Button>
            </ButtonGroup>
          ];
        }
      })
    : [];

  const headings = ['Date', 'Type', 'Title / Description', 'User', 'Actions'];

  return (
    <Card>
      <Box>
        <Box paddingBlockEnd="200">
          <InlineStack align="space-between">
            <CardHeading title="Employee Timeline" />
            <Button primary onClick={() => setIsNewModalOpen(true)}>
              New Event
            </Button>
          </InlineStack>
        </Box>

        {timelineItems.length > 0 ? (
          <DataTable columnContentTypes={Array(headings.length).fill('text')} headings={headings} rows={rows} verticalAlign="middle" increasedTableDensity />
        ) : (
          <div style={{ padding: '16px' }}>
            <Text>No timeline events found. Click "New Event" to create one.</Text>
          </div>
        )}
      </Box>

      {/* View/Edit Interaction Modal */}
      <Modal
        open={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setEditingInteraction(null);
          setModalTab(0);
        }}
        title="View Event"
        size="large"
      >
        <Modal.Section>
          {editingInteraction && editingInteraction.id ? (
            <>
              {/* Show banner if documents are attached */}
              {editingInteraction.S3ObjectKeys &&
                (() => {
                  try {
                    const keys = JSON.parse(editingInteraction.S3ObjectKeys);
                    const docCount = Array.isArray(keys) ? keys.length : 0;
                    if (docCount > 0) {
                      return (
                        <Box paddingBlockEnd="400">
                          <Banner tone="info">
                            <InlineStack gap="200" align="space-between" blockAlign="center">
                              <Text as="span">
                                {docCount} {docCount === 1 ? 'document' : 'documents'} attached to this event
                              </Text>
                              {modalTab === 0 && (
                                <Button size="slim" onClick={() => setModalTab(1)}>
                                  View documents
                                </Button>
                              )}
                            </InlineStack>
                          </Banner>
                        </Box>
                      );
                    }
                  } catch (e) {
                    // Invalid JSON, ignore
                  }
                  return null;
                })()}

              <Tabs
                tabs={[
                  {
                    id: 'details',
                    content: 'Details',
                    panelID: 'details-panel'
                  },
                  {
                    id: 'documents',
                    content: 'Documents',
                    panelID: 'documents-panel'
                  }
                ]}
                selected={modalTab}
                onSelect={handleModalTabChange}
              >
                {modalTab === 0 && (
                  <div id="details-panel" role="tabpanel">
                    <EmployeeInteractionForm interaction={editingInteraction} onSubmit={handleSaveInteraction} />
                  </div>
                )}
                {modalTab === 1 && editingInteraction.id && (
                  <div id="documents-panel" role="tabpanel">
                    <InteractionDocuments interactionId={editingInteraction.id} />
                  </div>
                )}
              </Tabs>
            </>
          ) : (
            <EmployeeInteractionForm interaction={editingInteraction} onSubmit={handleSaveInteraction} />
          )}
        </Modal.Section>
        <Modal.Section>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setIsViewModalOpen(false);
                setEditingInteraction(null);
                setModalTab(0);
              }}
            >
              Close
            </Button>
          </div>
        </Modal.Section>
      </Modal>

      {/* New Interaction Modal */}
      <Modal open={isNewModalOpen} onClose={() => setIsNewModalOpen(false)} title="New Employee Event" size="large">
        <Modal.Section>
          <EmployeeInteractionForm interaction={getInitialInteraction()} isNew={true} onSubmit={handleSaveInteraction} />
        </Modal.Section>
        <Modal.Section>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setIsNewModalOpen(false)}>Close</Button>
          </div>
        </Modal.Section>
      </Modal>
    </Card>
  );
}

// Simplified form component for employee interactions
function EmployeeInteractionForm({ interaction, isNew = false, onSubmit }) {
  const [formData, setFormData] = useState(interaction || {});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setFormData(interaction || {});
  }, [interaction]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.type) {
      setError('Please select an event type');
      return;
    }
    if (!formData.title || !formData.title.trim()) {
      setError('Please enter a title');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(formData);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to save event. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormLayout>
      {error && (
        <div style={{ padding: '12px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px' }}>
          <Text as="p" tone="critical">
            {error}
          </Text>
        </div>
      )}

      <Select
        label="Event Type"
        options={[{ label: 'Select type...', value: '' }, ...employeeInteractionTypes.map((t) => ({ label: t.label, value: t.value }))]}
        value={formData.type || ''}
        onChange={(value) => handleChange('type', value)}
      />

      <TextField label="Title" value={formData.title || ''} onChange={(value) => handleChange('title', value)} placeholder="Brief description of the event" autoComplete="off" />

      <TextField label="Event Date" type="datetime-local" value={formData.event_date || ''} onChange={(value) => handleChange('event_date', value)} autoComplete="off" />

      <div>
        <label style={{ fontWeight: 500, marginBottom: '8px', display: 'block' }}>Description</label>
        <ReactQuill theme="snow" value={formData.description || ''} onChange={(value) => handleChange('description', value)} style={{ height: '200px', marginBottom: '50px' }} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button primary onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting}>
          {isNew ? 'Create Event' : 'Save Changes'}
        </Button>
      </div>
    </FormLayout>
  );
}
